import React from 'react'
import { colors } from '~/design'
import styled from 'styled-components'
import useMedia from 'react-use/lib/useMedia'
import InfoIcon from '~/components/icons/ui/16px_round-e-info.svg'
import { Level } from '~/packages/legalEntityGeoprocessing/map/legalEntityGeoprocessingMapLevels.data'
import { useSearchParams } from 'react-router-dom'

const Header = styled.div`
  padding-left: 70px;
  background: ${colors.sebraeBlueDarker};

  @media (max-width: 768px) {
    padding-left: unset;
  }

  .top-card {
    background: ${colors.sebraeBlueDarker};
    display: flex;
    padding: 5px 0;
    align-items: center;
    justify-content: space-between;
    height: 70px;

    .top-card-title {
      margin-left: 48px;
      display: flex;
      flex-direction: column;
      line-height: normal;
      color: #fff;
      justify-content: center;
      margin-bottom: 4px;

      h1 {
        font-size: 24px;
        font-weight: 400;
        margin: 0;
      }

      @media (max-width: 768px) {
        margin-left: 0px;
      }
    }

    @media (max-width: 768px) {
      height: 100px;
      padding: unset;
      align-items: flex-start;
      padding-top: 15px;
    }

    @media (max-width: 400px) {
      height: 150px;
    }
  }

  .top-card-info-wrapper {
    display: flex;
    margin-left: 48px;

    .top-card-icon {
      display: flex;
      max-width: 20px;
      align-items: center;

      .tooltip-container {
        position: relative;
        display: inline-block;
      }

      .tooltip-text {
        visibility: hidden;
        width: 250px;
        background-color: #333;
        color: #fff;
        text-align: left;
        border-radius: 5px;
        padding: 10px 10px 10px 10px;
        position: absolute;
        top: 175%;
        transform: translateX(-28%);
        opacity: 0;
        transition: opacity 0.3s;
        z-index: 10;
        font-size: 14px;
      }

      .tooltip-container:hover .tooltip-text {
        visibility: visible;
        opacity: 1;
      }

      .tooltip-text ul {
        padding-left: 20px;
        margin: 10px 0 0;
      }

      .tooltip-text li {
        margin-bottom: 5px;
      }
    }

    .top-card-info {
      margin-left: 8px;
      display: flex;
      flex-direction: column;
      line-height: normal;
      color: #fff;
      justify-content: center;
      margin-bottom: 4px;

      @media (max-width: 768px) {
        margin-left: unset;
      }

      .description {
        font-size: 16px;
      }

      .title {
        font-size: 18px;
        font-weight: bold;
      }

      @media (max-width: 768px) {
        margin-left: 0px;
      }
    }
  }

  .entities-data-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: right;
    align-items: end;
    padding-right: 38px;

    .container {
      display: flex;
      flex-direction: column;

      .description {
        color: #58c4da;
        text-align: right;
        font-size: 13px;
        font-weight: 400;
      }

      .value {
        color: #fff;
        font-size: 18px;
        font-style: italic;
        font-weight: 700;
        text-align: right;
        padding-left: 10px;
      }
    }

    .tooltip-container {
      position: relative;
      display: inline-block;
    }

    .tooltip-text {
      visibility: hidden;
      width: 250px;
      background-color: #333;
      color: #fff;
      text-align: left;
      border-radius: 5px;
      padding: 10px 10px 5px 10px;
      position: absolute;
      top: 125%;
      left: 50%;
      transform: translateX(-50%);
      opacity: 0;
      transition: opacity 0.3s;
      z-index: 10;
      font-size: 14px;
    }

    .tooltip-container:hover .tooltip-text {
      visibility: visible;
      opacity: 1;
    }

    .tooltip-text ul {
      padding-left: 20px;
      margin: 10px 0 0;
    }

    .tooltip-text li {
      margin-bottom: 5px;
    }
  }

  @media (max-width: 768px) {
    left: 0px;
    width: 100%;
    z-index: 0;
    padding-left: 0px;
  }
`

const BackWrapper = styled.div`
  width: 50%;
  display: flex;
  justify-content: end;
  padding-right: 10rem;
`

const BackButton = styled.button`
  width: fit-content;
  height: fit-content;

  border: none;
  background-color: transparent;
  cursor: pointer;

  color: #58c4da;
  font-size: 16px;
  font-weight: 700;
  line-height: 23.3px;
`

export interface ContentProps {
  content: {
    height?: string
    display?: string
  }
}

export interface EntityDataProps {
  selectedEntities?: number
  availableEntities?: string
  outOfRangeTotal?: string
  suffix?: string
  definitionType?: string
  legalEntityActiveCount?: number
}

export default function TopCard({
  title,
  outOfRangeEntities,
  entityData,
  onToggleMenu,
  selectedLegalEntity,
  customStyles,
  currentLevel,
  definedOption,
}: {
  title?: string
  outOfRangeEntities?: number | string
  entityData?: EntityDataProps
  onToggleMenu?: any
  selectedLegalEntity?: {
    selectedLegalEntityId: string
    selectedLegalEntityName: string
  }
  customStyles?: ContentProps
  dataSource?: string
  setDataSource?: React.Dispatch<React.SetStateAction<string>>
  tabIndicators?: any
  isLoadingIndicatorsData?: boolean
  currentLevel?: Level
  definedOption?: any
}) {
  const isMobile = useMedia('(max-width: 768px)')

  const [searchParams] = useSearchParams()
  const highlightTypeParam = searchParams.get('highlightType')
  const highlightValueParam = searchParams.get('highlightValue')

  const extraTypes = ['census', 'profits', 'scores']

  const [firstValue, secondValue] =
    entityData &&
    (entityData?.definitionType === 'legalEntityAttendanceIndex' ||
      entityData?.definitionType === 'legalEntityAttendance' ||
      entityData?.definitionType === 'legalEntity' ||
      entityData?.definitionType === 'customized') &&
    entityData.outOfRangeTotal
      ? entityData.outOfRangeTotal
          ?.split(',')
          .map(value => Number(value.trim().replace(/[\.,]/g, '')))
      : []

  return title ||
    outOfRangeEntities ||
    entityData?.selectedEntities ||
    onToggleMenu ||
    selectedLegalEntity ? (
    <Header>
      <div className='top-card' style={customStyles ? customStyles.content : undefined}>
        {entityData?.definitionType === undefined && definedOption === undefined && !isMobile ? (
          <div className='top-card-title'>
            <h1>{title}</h1>
          </div>
        ) : null}
        <div className='top-card-info-wrapper'>
          {entityData &&
          entityData?.definitionType &&
          entityData?.definitionType !== 'customized' &&
          entityData?.definitionType === definedOption.type &&
          !extraTypes.includes(entityData.definitionType) ? (
            <div className='top-card-icon'>
              <div className='tooltip-container'>
                <InfoIcon width={14} height={14} fill='#FFF' />
                <div className='tooltip-text'>
                  {entityData?.definitionType === 'legalEntityAttendanceIndex' ? (
                    <ul>
                      <li>
                        O índice de cobertura considera no <strong>numerador</strong> todas as
                        empresas, incluindo as ativas, inaptas, suspensas, extintas e outras
                        categorias, para contabilizar corretamente o número de atendimentos.
                      </li>
                      <li>
                        Enquanto o <strong>denominador</strong> inclui empresas ativas no mapa.
                      </li>
                    </ul>
                  ) : null}

                  {/* MEI */}
                  {entityData?.definitionType === 'legalEntity' ? (
                    <ul>
                      <li>
                        O índice atual considera como <strong>numerador</strong> todas as empresas
                        ativas com porte MEI no mapa.
                      </li>
                      <li>
                        Enquanto o <strong>denominador</strong> inclui empresas ativas no mapa.
                      </li>
                    </ul>
                  ) : null}

                  {entityData?.definitionType === 'legalEntityAttendance' ? (
                    <ul>
                      <li>
                        O valor do <strong>total da seleção</strong> considera todas as empresas,
                        incluindo as ativas, inaptas, suspensas, extintas e outras categorias, para
                        contabilizar corretamente o número de atendimentos.
                      </li>
                    </ul>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          <div className='top-card-info'>
            {title && entityData?.definitionType && definedOption?.type && (
              <>
                {entityData.definitionType === 'legalEntityAttendanceIndex' &&
                  definedOption.type === 'legalEntityAttendanceIndex' && (
                    <span className='description'>Índice de cobertura </span>
                  )}

                {entityData.definitionType === 'legalEntityAttendance' &&
                  definedOption.type === 'legalEntityAttendance' && (
                    <span className='description'>Empresas atendidas </span>
                  )}

                {definedOption.type === 'legalEntity' &&
                ['customized', 'legalEntity', ...extraTypes].includes(entityData.definitionType) ? (
                  <span className='title' style={{ fontSize: '22px' }}>
                    {title}
                  </span>
                ) : (
                  (entityData.definitionType === 'legalEntityAttendanceIndex' ||
                    entityData.definitionType === 'legalEntityAttendance') && (
                    <span className='title'>{title}</span>
                  )
                )}
              </>
            )}
          </div>
        </div>

        {!selectedLegalEntity && (
          <div className='entities-data-wrapper' style={{}}>
            {outOfRangeEntities !== 'Carregando...' &&
            entityData?.availableEntities &&
            currentLevel?.geo !== 'neighborhood' &&
            entityData?.outOfRangeTotal &&
            entityData.definitionType !== 'census' &&
            entityData.definitionType !== 'scores' ? (
              <div className='tooltip-container'>
                <InfoIcon width={14} height={14} fill='#FFF' />
                <div className='tooltip-text'>
                  A seleção total inclui:
                  <ul style={{ paddingBottom: '10px' }}>
                    <li>
                      <strong>{entityData?.availableEntities.toLocaleString()}</strong> empresas{' '}
                      {entityData?.definitionType === 'customized' ? 'ativas' : null}{' '}
                      {entityData?.definitionType === 'legalEntityAttendance' ||
                      entityData?.definitionType === 'legalEntityAttendanceIndex'
                        ? 'atendidas'
                        : null}{' '}
                      disponíveis no mapa.
                    </li>
                    {Number(firstValue) > 0 &&
                    (entityData?.definitionType === 'legalEntityAttendance' ||
                      entityData?.definitionType === 'customized') ? (
                      <li>
                        <strong>{firstValue.toLocaleString('pt-BR')}</strong> empresas{' '}
                        {entityData?.definitionType === 'customized' ? 'ativas' : null}{' '}
                        {entityData?.definitionType === 'legalEntityAttendance'
                          ? 'atendidas'
                          : null}{' '}
                        indisponíveis no mapa por endereçamento limitado.
                      </li>
                    ) : null}
                    {Number(firstValue) > 0 &&
                    (entityData?.definitionType === 'legalEntityAttendanceIndex' ||
                      entityData?.definitionType === 'legalEntity') ? (
                      <li>
                        <strong>{firstValue.toLocaleString('pt-BR')}</strong> empresas atendidas
                        indisponíveis no mapa por endereçamento limitado.
                      </li>
                    ) : null}
                  </ul>
                  {Number(secondValue) > 0 &&
                  (entityData?.definitionType === 'legalEntityAttendanceIndex' ||
                    entityData?.definitionType === 'legalEntity') ? (
                    <>
                      A seleção total inclui também na contagem de seu denominador:
                      <ul>
                        <li>
                          <strong>
                            {entityData.legalEntityActiveCount?.toLocaleString('pt-BR')}
                          </strong>{' '}
                          empresas ativas disponíveis no mapa
                        </li>
                        <li>
                          <strong>{secondValue.toLocaleString('pt-BR')}</strong> empresas ativas
                          indisponíveis no mapa por endereçamento limitado.
                        </li>
                      </ul>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}

            {currentLevel?.geo === 'neighborhood' ? (
              <div className='tooltip-container'>
                <InfoIcon width={14} height={14} fill='#FFF' />
                <div className='tooltip-text'>
                  A contagem atual pode diferir do nível anterior. No nível de bairro, mostramos
                  empresas específicas do bairro, enquanto no nível de ruas, consideramos ruas que
                  podem cruzar outros bairros, aumentando o total.
                </div>
              </div>
            ) : null}

            {(entityData && highlightTypeParam === null && highlightValueParam === null) ||
            (entityData && highlightTypeParam !== null && highlightValueParam !== null) ? (
              <div className='container' style={{}}>
                <span className='description'>Total da seleção</span>
                <span className='value'>
                  {entityData?.outOfRangeTotal === 'Carregando...' ||
                  entityData?.selectedEntities === undefined ||
                  (entityData &&
                    entityData.definitionType &&
                    !extraTypes.includes(entityData.definitionType) &&
                    Number.isNaN(Number(entityData?.selectedEntities)))
                    ? 'Carregando...'
                    : `${
                        entityData.definitionType === 'census'
                          ? entityData.availableEntities
                          : entityData.definitionType === 'scores'
                            ? `Média: ${entityData.selectedEntities}`
                            : entityData.selectedEntities?.toLocaleString('pt-BR')
                      } ${entityData?.suffix}`}
                </span>
              </div>
            ) : null}
          </div>
        )}

        {selectedLegalEntity && (
          <BackWrapper>
            <BackButton onClick={onToggleMenu}>{'<'} Voltar a página anterior</BackButton>
          </BackWrapper>
        )}
      </div>
    </Header>
  ) : null
}
